import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePostData from "../../../../../../CustomHooks/useAxios/usePostData";
import { FACTORY_CHECKPOINT_UPDATE } from "../../../../../../api/allApi_List";
import { setJobCardData } from "../../../../../../redux/jobCardDataSlice";
import CheckpointField from "../CheckpointItem/CheckpointField";
import CheckpointSubmit from "../CheckpointItem/CheckpointSubmit";
import CheckpointCompleted from "../CheckpointItem/CheckpointCompleted";
import CheckpointHeader from "../CheckpointItem/CheckpointHeader";
import DiscardMessage from "../CheckpointItem/DiscardMessage";
import { areCheckpointDependenciesSatisfied } from "../../../../utils/jobCardDependencyUtils";
import DiscardedSuccess from "../CheckpointItem/DiscardedSuccess";
import ErrorDisplay from "../../../../../../components/UI/ErrorDisplay";
import useDebounce from "../.././../../../../CustomHooks/useDebounce";
import { FACTORY_CHECKPOINT_PERMISSIONS } from "../../../../constants/checkpointPermissions";
import UsePermissionsClaim from "../../../../../../CustomHooks/UsePermissionsClaim";

const FifthCheckPointItem = ({ checkpoint, updatedCheckPoints }) => {
  const [value, setValue] = useState("");
  const debouncedWeight = useDebounce(value, 500); // wait 500ms
  const { executePost, loading, formError, setFormError, successResponse } =
    usePostData();
  const jobCardData = useSelector((state) => state.jobCard.data);
  const dispatch = useDispatch();

  // Current Checkpoint data
  const { id, name, description, dependsOnIds } = checkpoint;

  // Job card Data
  const {
    cylinderId,
    cylinder: { actualWeight },
    checkpointIds,
  } = jobCardData;

  // Get Permission by ID and Check
  const { checkPermission } = UsePermissionsClaim();
  const checkPerm = checkPermission(FACTORY_CHECKPOINT_PERMISSIONS[id]);

  // find Current Checkpoint updated data
  const updatedCheckPoint = updatedCheckPoints?.find(
    (cp) => cp.checkpointId === id,
  );
  const { resultVal, status } = updatedCheckPoint ?? {};

  // check if current checkpoint is completed
  const isCompleted = status === 1;

  // Parse weights safely
  const actual = parseFloat(actualWeight);
  const current = parseFloat(debouncedWeight);
  const is5PercentLess = (base, value) => base && value && value < base * 0.95;
  const currentLessThan5 = is5PercentLess(actual, current);

  // Verify whether all required dependent checkpoints
  // are completed before enabling this checkpoint
  const isDependencyValid = areCheckpointDependenciesSatisfied(
    dependsOnIds,
    checkpointIds,
  );

  // Final condition to enable the checkpoint:
  // 1. User must have permission (checkPerm)
  // AND
  // 2. Either all dependencies are satisfied
  const isCheckpointEnabled = isCompleted || (checkPerm && isDependencyValid);

  // API calling for checkpoint action
  const sendData = async () => {
    const updateStateOnResponse = (data) => {
      dispatch(setJobCardData(data));
    };
    executePost(
      {},
      `${FACTORY_CHECKPOINT_UPDATE}${id}/${cylinderId}/${value}`,
      updateStateOnResponse,
    );
  };

  return (
    <div
      className={`group bg-white rounded-xl p-4 py-2 max-md:p-2  shadow-sm hover:shadow-md transition border-1 relative ${borderStyle[status]}`}
    >
      {id === 21 && (
        <DiscardMessage
          discardOn={currentLessThan5}
          message="As per regulatory guidelines, cylinder will be discarded due to a weight loss exceeding 5% of its original weight."
        />
      )}
      {/* Header */}
      <CheckpointHeader name={name} description={description} />
      <div className="flex justify-between items-center">
        {/* Input */}
        <div
          className={`mt-2 md:my-4 ${isCheckpointEnabled ? "" : "disable-bx"}`}
        >
          <CheckpointField
            checkpoint={checkpoint}
            value={resultVal ?? value}
            onChange={setValue}
            readOnly={isCompleted}
          />
        </div>
        {/* Actions */}
        {!resultVal && (
          <CheckpointSubmit
            value={value}
            loading={loading}
            onSubmit={sendData}
          />
        )}
        {/* Checkpoint completed status */}
        {isCompleted && <CheckpointCompleted />}
      </div>

      {/* Discarded Success */}
      {successResponse?.status === 1 && currentLessThan5 && (
        <DiscardedSuccess cylinderSerialNo={cylinderId} />
      )}
      {formError && (
        <div className="flex">
          <ErrorDisplay
            message={formError}
            closeAction={() => setFormError("")}
          />
        </div>
      )}
    </div>
  );
};

export default FifthCheckPointItem;

export const borderStyle = {
  0: "border-gray-200",
  1: "border-teal-100",
};
